"use client";

import { CSSProperties, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useIsMobile } from "@hooks/use-mobile";
import assetLoader from "@utils/internalImageLoder";
import styles from "./CustomerFeedbackCarouselSection.module.scss";

interface CustomerFeedbackCarouselMedia {
  url: string;
  alt: string;
}

interface CustomerFeedbackCarouselItem {
  text: string;
  name: string;
  location: string;
  date?: string;
  image?: CustomerFeedbackCarouselMedia | null;
}

interface CustomerFeedbackCarouselSectionProps {
  title: string;
  items: CustomerFeedbackCarouselItem[];
}

const DESKTOP_VISIBLE_COUNT = 4;
const MOBILE_VISIBLE_COUNT = 1;

const formatFeedbackDate = (value?: string) => {
  if (!value) return "";

  const parts = value.split("-");
  if (parts.length !== 3) return value;

  const [year, month, day] = parts;
  if (!year || !month || !day) return value;

  return `${day}-${month}-${year}`;
};

export default function CustomerFeedbackCarouselSection({
  title,
  items,
}: CustomerFeedbackCarouselSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const isMobile = useIsMobile();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const scrollTargetIndexRef = useRef<number | null>(null);
  const scrollReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [startIndex, setStartIndex] = useState(0);

  const visibleCount = isMobile ? MOBILE_VISIBLE_COUNT : DESKTOP_VISIBLE_COUNT;
  const canScroll = items.length > visibleCount;
  const hasSingleItem = items.length === 1;
  const maxStartIndex = Math.max(0, items.length - visibleCount);
  const displayCount = Math.min(startIndex + visibleCount, items.length);
  const desktopColumnCount = Math.min(Math.max(items.length, 1), DESKTOP_VISIBLE_COUNT);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const syncIndexFromScroll = () => {
    if (!scrollerRef.current || itemRefs.current.length === 0) return;

    if (!isMobile && scrollTargetIndexRef.current !== null) {
      setStartIndex(scrollTargetIndexRef.current);
      return;
    }

    const scrollLeft = scrollerRef.current.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    itemRefs.current.forEach((item, index) => {
      if (!item) return;
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setStartIndex(Math.min(closestIndex, maxStartIndex));
  };

  useEffect(() => {
    syncIndexFromScroll();
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (scrollReleaseTimerRef.current) {
        clearTimeout(scrollReleaseTimerRef.current);
      }
    };
  }, []);

  const scrollToIndex = (index: number) => {
    const safeIndex = Math.max(0, Math.min(index, maxStartIndex));
    const target = itemRefs.current[safeIndex];

    if (!target) return;

    if (!isMobile) {
      scrollTargetIndexRef.current = safeIndex;
      if (scrollReleaseTimerRef.current) {
        clearTimeout(scrollReleaseTimerRef.current);
      }
      scrollReleaseTimerRef.current = setTimeout(() => {
        scrollTargetIndexRef.current = null;
        syncIndexFromScroll();
      }, 450);
    }

    target.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });

    setStartIndex(safeIndex);
  };

  const handlePrev = () => {
    const step = isMobile ? 1 : DESKTOP_VISIBLE_COUNT;
    scrollToIndex(Math.max(startIndex - step, 0));
  };

  const handleNext = () => {
    const step = isMobile ? 1 : DESKTOP_VISIBLE_COUNT;
    scrollToIndex(Math.min(startIndex + step, maxStartIndex));
  };

  return (
    <div className={styles.section}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>{title}</h2>
        {isHydrated && !isMobile && canScroll ? (
          <div className={styles.controls}>
            <div className={styles.counter}>{`${displayCount}/${items.length}`}</div>
            <div className={styles.buttonRow}>
              <button
                type="button"
                className={styles.navButton}
                onClick={handlePrev}
                disabled={startIndex === 0}
                aria-label="Previous feedback"
              >
                <Image
                  src={`${assetLoader()}/icons/Carousal/ArrowLeft.svg`}
                  alt=""
                  width={20}
                  height={20}
                  className={styles.arrowIcon}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={handleNext}
                disabled={startIndex >= maxStartIndex}
                aria-label="Next feedback"
              >
                <Image
                  src={`${assetLoader()}/icons/Carousal/ArrowRight.svg`}
                  alt=""
                  width={20}
                  height={20}
                  className={styles.arrowIcon}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div
        ref={scrollerRef}
        className={`${styles.scroller} ${hasSingleItem ? styles.singleItemScroller : ""}`}
        onScroll={syncIndexFromScroll}
      >
        {items.map((item, index) => (
          <article
            key={`${item.name}-${index}`}
            ref={(node) => {
              itemRefs.current[index] = node;
            }}
            className={`${styles.card} ${item.image?.url ? styles.cardWithImage : styles.cardWithoutImage}`}
            style={
              {
                "--desktop-feedback-columns": desktopColumnCount,
              } as CSSProperties
            }
          >
            {item.image?.url ? (
              <div className={styles.mediaWrap}>
                <Image
                  src={item.image.url}
                  alt={item.image.alt || item.name}
                  fill
                  sizes="(max-width: 767px) 100vw, 38vw"
                  className={styles.media}
                />
              </div>
            ) : null}

            <div className={styles.content}>
              <Image
                src={`${assetLoader()}/icons/startQuote.webp`}
                alt=""
                width={28}
                height={24}
                className={styles.quoteIconStart}
              />
              <p className={styles.text}>{item.text}</p>
              <div className={styles.meta}>
                <p className={styles.name}>{item.name}</p>
                <p className={styles.location}>
                  {[item.location, formatFeedbackDate(item.date)]
                    .filter(Boolean)
                    .join(" | ")}
                </p>
              </div>
              <Image
                src={`${assetLoader()}/icons/endQuote.webp`}
                alt=""
                width={28}
                height={24}
                className={styles.quoteIconEnd}
              />
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
